import { useContext, useEffect, useRef, useState } from "react";
import { sharedLib } from "@shared/services/shared.service";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  AuthUser,
  LanguageNameSpaces,
  QueryStatus,
  UserImpersonationPermissionLevel,
  UserProperties,
  WorkspaceType,
} from "@shared/types";
import { SymbolsMetadataContext } from "@shared/features/common/SymbolsMetadata.context";
import { USAGE_TYPES } from "@shared/features/settings/constants";
import { selectWorkspace } from "@shared/slices/common.slice";
import { ClientsDataContext } from "@shared/features/common/ClientsData.context";
import { ExchangesMetadataContext } from "@shared/features/common/ExchangesMetadata.context";
import { useBranding } from "@shared/utils/hooks/useBranding";
import { formatDate } from "@shared/utils/date";
import { SECONDS_IN_DAY } from "@shared/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TelegramWebAppContext } from "@shared/features/common/TelegramWebApp.context";
import { TypographyType } from "@shared/common/Typography/Typography";

export const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>(undefined);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

export const useGetUserQuery = (isDemo?: boolean, skip?: boolean) => {
  const token = useSelector(sharedLib.selectors.authentication.selectToken);
  return sharedLib.sharedApi.authentication.useGetUserQuery(
    !skip && token
      ? {
          is_demo_query: !!isDemo,
        }
      : skipToken
  );
};

export const useGetUser = (skip?: boolean) => {
  const workspace = useSelector(selectWorkspace);
  const getRealUserQuery = useGetUserQuery(false, skip);
  const getDemoUserQuery = useGetUserQuery(true, skip);

  const refetch = () => {
    try {
      getRealUserQuery.refetch();
    } catch {}
    try {
      getDemoUserQuery.refetch();
    } catch {}
  };

  const queryData =
    workspace === WorkspaceType.DEMO ? getDemoUserQuery : getRealUserQuery;
  return { ...queryData, refetch };
};

export const useUser = (skip?: boolean) => {
  const { isLoading, isFetching, isError, refetch } = useGetUser(skip);
  const user = useSelector(sharedLib.selectors.authentication.selectUser);

  return {
    user,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
};

export const useSelectUser = () => {
  return useSelector(sharedLib.selectors.authentication.selectUser);
};

export const useSelectRealUser = () => {
  return useSelector(sharedLib.selectors.authentication.selectRealUser);
};

export const useIsSubUser = () => {
  const user = useSelectUser();
  return user?.is_sub_user;
};

export const useSubUserPermissions = () => {
  const user = useSelectUser();
  const isSubUser = user?.is_sub_user;
  return {
    isSubUser,
    isReadOnly:
      isSubUser &&
      user?.sub_user_permission_level === UserImpersonationPermissionLevel.READ,
    isReadWrite:
      isSubUser &&
      user?.sub_user_permission_level ===
        UserImpersonationPermissionLevel.WRITE,
  };
};

interface useSharedIntercomProps {
  logout: () => void;
  update: () => void;
  login: () => void;
  isReady?: boolean;
  skip?: boolean;
}

export const useSharedIntercom = ({
  logout,
  login,
  update,
  isReady = true,
  skip,
}: useSharedIntercomProps) => {
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loggedInUserId, setLoggedInUserId] = useState<number | undefined>(
    undefined
  );
  const functionsRef = useRefValue({ logout, login, update });

  useEffect(() => {
    if (!isReady || skip) return;
    let shouldBoot = !isInitialized;
    const userChanged =
      isInitialized && user && loggedInUserId && user.id !== loggedInUserId;
    const userLoggedOut = isInitialized && !user && loggedInUserId;
    const userUpdated =
      isInitialized && user && loggedInUserId && user.id === loggedInUserId;
    const userLoggedIn = isInitialized && user && !loggedInUserId;
    if (userChanged || userLoggedOut) {
      functionsRef.current.logout();
      shouldBoot = true;
    } else if (userUpdated) {
      functionsRef.current.update();
      shouldBoot = false;
    } else if (userLoggedIn) shouldBoot = true;
    if (shouldBoot) {
      functionsRef.current.login();
      setIsInitialized(true);
      setLoggedInUserId(user?.id);
    }
  }, [user?.id, isReady, skip]);

  return isInitialized;
};

export const useQueryWithRetries = <Data = any, Args = any>(
  queryHook: (args?: Args, options?: any) => QueryStatus<Data, Args>,
  args?: Args,
  options?: any
) => {
  const [numRetries, setNumRetries] = useState(0);
  const [delay, setDelay] = useState(2000);
  const queryStatus = queryHook(args, options);
  const queryStatusRef = useRef<QueryStatus>(undefined);
  const isLoadingRef = useRef<boolean>(undefined);
  queryStatusRef.current = queryStatus;
  isLoadingRef.current = queryStatus.isFetching || queryStatus.isLoading;

  const resetState = () => {
    setNumRetries(0);
    setDelay(2000);
  };

  const retryQuery = () => {
    setTimeout(() => {
      try {
        if (queryStatusRef.current?.isError && !isLoadingRef.current) {
          queryStatusRef?.current?.refetch();
        }
      } catch {}
    }, delay);
  };

  useEffect(() => {
    if (isLoadingRef.current) return;
    if (queryStatusRef.current?.isError && numRetries < 7) {
      retryQuery();
      setNumRetries((numRetries) => numRetries + 1);
      setDelay((delay) => delay * 2);
    } else if (queryStatusRef.current?.isSuccess) {
      resetState();
    }
  }, [queryStatus]);

  return queryStatus;
};

export const useSymbolsMetadata = () => useContext(SymbolsMetadataContext)!;

export const useUserUsageTypes = () => {
  return USAGE_TYPES;
};

export const useClientsData = () => useContext(ClientsDataContext)!;

export const useExchangesMetadata = () => useContext(ExchangesMetadataContext)!;

export const useDisplayTradeDetails = () => {
  const { brandingData } = useBranding();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const displayTradeDetails = brandingData?.display_trade_details ?? false;
  return !user?.is_sub_user || displayTradeDetails;
};

export const useTelegramWebAppData = () => useContext(TelegramWebAppContext)!;

type UserInfoItem = {
  key: string;
  value: string;
  type: TypographyType;
};

export const useUserInfo = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { user } = useUser();
  const currentUnixTimestamp = Date.now() / 1000;
  const userExpirationTimestamp = user?.expiration_timestamp || 0;
  const expirationDate = formatDate(userExpirationTimestamp, false, true);
  const daysLeft =
    (userExpirationTimestamp - currentUnixTimestamp) / SECONDS_IN_DAY;

  const userInfoItems: UserInfoItem[] = [];
  const uidItem: UserInfoItem | undefined = !user?.isWhopExperience()
    ? {
        key: "UID",
        value: user?.uid?.toString() ?? "",
        type: "secondary",
      }
    : undefined;

  if (!user?.isWhopExperience()) {
    if (user?.package) {
      userInfoItems.push({
        key: t("user_info.plan_label"),
        value: user.getPackageDisplay(),
        type: "secondary",
      });
    }
    if (!user?.isFree() && !!user?.expiration_timestamp) {
      userInfoItems.push({
        key: user?.isExpired()
          ? t("user_info.expired_on")
          : t("user_info.expires_on"),
        value: expirationDate,
        type: daysLeft < 1 ? "danger" : "secondary",
      });
    }
  }

  return {
    userInfoItems,
    uidItem,
  };
};

export const useLiveGroupSearch = (subGroupId?: number) => {
  const liveUpdateChannelsQuery =
    sharedLib.sharedApi.configuration.useLiveUpdateChannelsQuery(
      subGroupId ? { sub_group_id: subGroupId } : skipToken
    );
  const groupsData = liveUpdateChannelsQuery.data?.data.data.groups_data;
  const isNotMember =
    !!subGroupId && !liveUpdateChannelsQuery.isFetching && !groupsData?.length;

  return {
    data: liveUpdateChannelsQuery.data,
    isNotMember,
    isFetching: liveUpdateChannelsQuery.isFetching,
    liveUpdateChannelsQuery,
  };
};

export const useRefValue = <T extends any>(value: T) => {
  const valueRef = useRef<T>(value);
  valueRef.current = value;
  return valueRef;
};

export const getRedirectConnectWhopChannel = (
  user: AuthUser | UserProperties
) => {
  return (
    user.is_whop_creator &&
    !user.has_connected_whop_groups &&
    !user.is_whop_cornix_product
  );
};

export const getRedirectWhopCreateBot = (user?: AuthUser | UserProperties) => {
  return (
    user?.is_whop_user &&
    !user?.is_whop_creator &&
    !user?.user_auto_trading_enabled
  );
};
